import {Button, Dialog, DialogBody, DialogFooter, Text, TextArea} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Fragment, useState} from "react";
import {AssignmentState} from "../../../../providers/AssignmentReducer";
import {DriverRequirementEnforcementLevel} from "../../../settings/enums/DriverRequirementEnforcementLevel";
import "./AssignJobsConfirmation.css";
import {useDispatchDataContext} from "../../../../providers/DispatchDataProvider";
import {getRequirementGap, truncateSkillList} from "../../../../utils/assignment/Assignment.utils";

const MAX_NOTE_LENGTH = 255;
const MIN_REASON_LENGTH = 50;

const DialogBox = styled(Dialog)`
  box-sizing: border-box;
  background: #fcfcfc;
  box-shadow: 0 7px 20px rgba(0, 0, 0, 0.35);
  border-radius: 4px;
  width: 390px;
  &.prevent {
    background: #ffdee0;
    .bp5-dialog-header {
      background: #ffdee0;
    }
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  padding-left: 13px;
  padding-top: 13px;
`;

const Title = styled(Text)`
  font-family: "Roboto", serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  padding-left: 19px;
  color: #161616;
`;

const HeaderIcon = styled(FontAwesomeIcon)``;

const Body = styled(DialogBody)`
  background: transparent;
  font-family: "Roboto", serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18.51px;

  padding: 9px 28px 14px;
`;

const BodyText = styled(Text)`
  color: #161616;
  &.body-text-bottom {
    padding-right: 4px;
  }
`;

const BodyTextCenter = styled(BodyText)`
  padding: 24px 0;
`;

const TextDetail = styled.div`
  padding-bottom: 12px;
  &:last-child {
    padding-bottom: 0;
  }
`;

const JobNumberText = styled(Text)`
  color: #0000ee;
  display: inline;
`;

const RequireMentText = styled(Text)`
  color: #fa545e;
  display: inline;
  font-style: italic;
`;

const JobLine = styled.div``;

const MissingLine = styled.div`
  margin-top: 4px;
`;

const ReasonTextArea = styled(TextArea)`
  width: 100%;
  min-height: 48px;
  margin-top: 6px;
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  resize: vertical;
`;

const ReasonLabel = styled(Text)`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const Footer = styled(DialogFooter)`
  background: transparent;
  border-radius: 0 0 0 0;
  border-top: 0;
  padding: 0 28px 18px;
`;

const ConfirmButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  background: #ffffff !important;
  box-shadow: 0px 2px 2px 0px #00000040 !important;
  border-radius: 4px;
  border: none;
  outline: none;
  font-family: "Roboto", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  &.button-no {
    color: #161616;
  }
  &:hover {
    background: rgba(143, 153, 168, 0.15) !important;
  }
  &:disabled {
    :hover {
      background: #ffffff !important;
    }
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export interface AssignJobsConfirmationProps {
  testIdPrefix: string;
  isVisible: boolean;
  driverRequiredSetting: string;
  assignmentState: AssignmentState;
  isSkillsBasedRequirements: boolean;
  bodyText?: string;
  warningButtonLabels?: {cancel: string; confirm: string};
  portalClassName?: string;
  handleConfirm(reasons: Record<number, string>): void;
  handleClose(): void;
}

export const AssignJobsConfirmation = ({
  testIdPrefix,
  isVisible,
  assignmentState,
  driverRequiredSetting,
  isSkillsBasedRequirements = false,
  bodyText,
  warningButtonLabels,
  portalClassName,
  handleConfirm,
  handleClose
}: AssignJobsConfirmationProps) => {
  const {skills} = useDispatchDataContext();
  const isReassignAction = assignmentState.selectedStops.length > 0;
  const isWarningMode = driverRequiredSetting === DriverRequirementEnforcementLevel.Warning;
  const requiresOverrideReason = isWarningMode && isSkillsBasedRequirements;
  const itemLabel = isSkillsBasedRequirements ? "skill(s)" : "requirement(s)";

  const [reasonsByJobId, setReasonsByJobId] = useState<Record<number, string>>({});

  const updateReason = (jobId: number, reason: string) => {
    setReasonsByJobId((prev) => ({...prev, [jobId]: reason}));
  };

  // Reassignment selects stops; list each job once.
  const selectedRows = isReassignAction
    ? Array.from(new Map(assignmentState.selectedStops.map((stop) => [stop.job.jobNumber, stop])).values()).map(
        (stop) => ({jobId: stop.job.jobId, jobNumber: stop.job.jobNumber, order: stop.order})
      )
    : assignmentState.selectedJobs.map((job) => ({jobId: job.jobId, jobNumber: job.jobNumber, order: job.order}));

  const mismatchedJobs = selectedRows
    .map((row) => ({
      ...row,
      ...getRequirementGap({
        order: row.order,
        driver: assignmentState.selectedManifest?.driver,
        isSkillsBasedRequirements,
        activeSkills: skills
      })
    }))
    .filter((job) => job.missing.length > 0);

  const mismatchedJobIds = mismatchedJobs.map((job) => job.jobId);

  const allReasonsProvided =
    !requiresOverrideReason || mismatchedJobIds.every((jobId) => (reasonsByJobId[jobId] ?? "").trim().length > 0);

  const onConfirm = () => {
    handleConfirm(reasonsByJobId);
    setReasonsByJobId({});
  };

  const onClose = () => {
    handleClose();
    setReasonsByJobId({});
  };

  const actions = isWarningMode ? (
    <Fragment>
      <ConfirmButton
        className="button-no"
        data-testid={`${testIdPrefix}-dialog-no-button`}
        text={warningButtonLabels?.cancel ?? "No"}
        onClick={onClose}
      />
      <ConfirmButton
        data-testid={`${testIdPrefix}-dialog-yes-button`}
        text={warningButtonLabels?.confirm ?? "Yes, Assign"}
        style={{color: "#fa545e"}}
        onClick={onConfirm}
        disabled={!allReasonsProvided}
      />
    </Fragment>
  ) : (
    <ConfirmButton
      className="button-no"
      data-testid={`${testIdPrefix}-dialog-no-button`}
      text={"Close"}
      onClick={onClose}
    />
  );

  const title = isWarningMode ? (
    <Header>
      <HeaderIcon data-testid={"header-icon"} color={"#FF6B00"} size={"xl"} icon={solid("circle-exclamation")} />
      <Title data-testid={"header-title"}>{`Assign to ${assignmentState?.selectedManifest?.driver?.name}?`}</Title>
    </Header>
  ) : (
    <Header>
      <HeaderIcon data-testid={"header-icon"} color={"#FA545E"} size={"xl"} icon={solid("triangle-exclamation")} />
      <Title data-testid={"header-title"}>Can&apos;t Assign to This Driver</Title>
    </Header>
  );

  const renderQuotedNames = (items: Array<{id: number; name?: string | null}>) =>
    items.map((item, index) => (
      <Fragment key={item.id}>
        <RequireMentText>&apos;{item.name}&apos;</RequireMentText>
        {index < items.length - 1 && "; "}
      </Fragment>
    ));

  const bodyJobs = mismatchedJobs.map(({jobId, jobNumber, required, missing}) => {
    const reason = reasonsByJobId[jobId] ?? "";
    const maxPrefixLength = MAX_NOTE_LENGTH - MIN_REASON_LENGTH;
    const skillList = truncateSkillList(
      missing.map((item) => item.name ?? ""),
      maxPrefixLength
    );
    const effectiveMax = MAX_NOTE_LENGTH - `Missing: ${skillList} | `.length;
    return (
      <TextDetail key={jobNumber}>
        <JobLine data-testid={`${testIdPrefix}-job-line-${jobId}`}>
          Job <JobNumberText>{jobNumber}</JobNumberText> - {renderQuotedNames(required)}
        </JobLine>
        <MissingLine data-testid={`${testIdPrefix}-missing-${jobId}`}>
          Driver is missing {renderQuotedNames(missing)} {itemLabel}
        </MissingLine>
        {requiresOverrideReason && (
          <>
            <ReasonLabel>
              Reason<span style={{color: "#fa545e"}}>*</span>:
            </ReasonLabel>
            <ReasonTextArea
              data-testid={`${testIdPrefix}-reason-${jobId}`}
              placeholder="Please enter the override reason"
              value={reason}
              onChange={(e) => updateReason(jobId, e.target.value)}
              maxLength={effectiveMax}
            />
          </>
        )}
      </TextDetail>
    );
  });

  const bodyTextBottom = isWarningMode
    ? "Would you like to proceed with assignment anyway?"
    : "If you feel this is by mistake, please navigate to 'Driver Information' within CMS to update this driver's record";

  return (
    <DialogBox
      data-testid={`${testIdPrefix}-dialog-box`}
      title={title}
      isOpen={isVisible}
      onClose={onClose}
      className={`assign-jobs-confirmation-dialog ${driverRequiredSetting.toLowerCase()}`}
      portalClassName={portalClassName}
    >
      <Body>
        <BodyText>
          {bodyText ??
            `The job(s) have ${itemLabel} that the selected driver does not have based on our system's record.`}
        </BodyText>
        <BodyTextCenter>{bodyJobs}</BodyTextCenter>
        <BodyText data-testid={"body-text-bottom"} className="body-text-bottom">
          {bodyTextBottom}
        </BodyText>
      </Body>
      <Footer actions={actions}></Footer>
    </DialogBox>
  );
};
